import type React from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import type { Permission, Role } from '@tekvoice/shared';
import { useAuth } from '@/auth/auth-context';
import { Skeleton } from '@/components/ui/skeleton';

function LoadingScreen(): React.JSX.Element {
  return (
    <div className="flex min-h-screen items-center justify-center">
      <div className="w-64 space-y-3">
        <Skeleton className="h-4 w-32" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-3/4" />
      </div>
    </div>
  );
}

/** Redirects to /login (remembering where the user was heading) when unauthenticated. */
export function ProtectedRoute(): React.JSX.Element {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) return <LoadingScreen />;
  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  }
  return <Outlet />;
}

/** SPEC §9 — SUPER_ADMIN-only sections. */
export function RequireRole({ role }: { role: Role }): React.JSX.Element {
  const { user, isLoading } = useAuth();
  if (isLoading) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== role) return <Navigate to="/" replace />;
  return <Outlet />;
}

/**
 * SPEC §9 — routes gated by the shared permission matrix rather than a role, so
 * EMPLOYEEs keep the read-only settings they are allowed to see.
 */
export function RequirePermission({ permission }: { permission: Permission }): React.JSX.Element {
  const { user, isLoading, can } = useAuth();
  if (isLoading) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" replace />;
  if (!can(permission)) return <Navigate to="/" replace />;
  return <Outlet />;
}
